import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import projectFileService from '../../../services/projectFileService';

const ProjectFileManager = ({ projectId, isOwner }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [fileUrls, setFileUrls] = useState({});
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [showTagInput, setShowTagInput] = useState(false);
  const [newTags, setNewTags] = useState('');
  const [editingTags, setEditingTags] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchFiles();
    fetchTags();
  }, [projectId, selectedTag]);

  const fetchFiles = async () => {
    setLoading(true);
    const data = await projectFileService?.getProjectFiles(projectId, selectedTag);
    setFiles(data || []);
    setLoading(false);

    // Load URLs for files
    data?.forEach(async (file) => {
      if (file?.file_path) {
        const url = await projectFileService?.getFileUrl(file?.file_path);
        if (url) {
          setFileUrls(prev => ({ ...prev, [file?.id]: url }));
        }
      }
    });
  };

  const fetchTags = async () => {
    const tags = await projectFileService?.getProjectTags(projectId);
    setAvailableTags(tags || []);
  };

  const handleFileUpload = async (event) => {
    const selectedFiles = Array.from(event?.target?.files || []);
    if (selectedFiles?.length === 0) return;

    setUploading(true);

    // Parse tags from input
    const tags = newTags?.split(',')?.map(t => t?.trim())?.filter(t => t?.length > 0) || [];

    for (const file of selectedFiles) {
      const { error } = await projectFileService?.uploadFile(
        projectId,
        file,
        tags
      );

      if (error) {
        alert(`Failed to upload ${file?.name}: ${error}`);
      }
    }

    setUploading(false);
    setNewTags('');
    setShowTagInput(false);
    await fetchFiles();
    await fetchTags();
    event.target.value = '';
  };

  const handleDeleteFile = async (file) => {
    if (!confirm(`Are you sure you want to delete "${file?.name}"?`)) return;

    const { error } = await projectFileService?.deleteFile(
      file?.id,
      file?.file_path
    );

    if (error) {
      alert(`Failed to delete: ${error}`);
    } else {
      await fetchFiles();
      await fetchTags();
    }
  };

  const handleDownloadFile = async (file) => {
    const { error } = await projectFileService?.downloadFile(
      file?.file_path,
      file?.name
    );

    if (error) {
      alert(`Failed to download: ${error}`);
    }
  };

  const handleUpdateTags = async (fileId, newTagsArray) => {
    const { error } = await projectFileService?.updateFileTags(fileId, newTagsArray);

    if (error) {
      alert(`Failed to update tags: ${error}`);
    } else {
      setEditingTags(null);
      await fetchFiles();
      await fetchTags();
    }
  };

  const handleSearch = async () => {
    if (!searchTerm?.trim()) {
      await fetchFiles();
      return;
    }

    setLoading(true);
    const results = await projectFileService?.searchFiles(projectId, searchTerm?.trim());
    setFiles(results || []);
    setLoading(false);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i))?.toFixed(1)} ${sizes?.[i]}`;
  };

  const getFileIcon = (file) => {
    const mimeType = file?.mime_type || '';
    if (mimeType?.startsWith('image/')) return 'Image';
    if (mimeType?.includes('pdf')) return 'FileText';
    if (mimeType?.includes('word') || mimeType?.includes('document')) return 'FileText';
    if (mimeType?.includes('excel') || mimeType?.includes('sheet')) return 'FileSpreadsheet';
    if (mimeType?.includes('zip')) return 'Archive';
    return 'File';
  };

  return (
    <div className="bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="FolderOpen" size={20} className="text-[var(--color-primary)]" />
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">Project Files</h3>
        </div>
        <div className="flex items-center gap-2">
          <label>
            <input
              type="file"
              multiple
              onChange={handleFileUpload}
              className="hidden"
              disabled={uploading}
            />
            <Button
              size="sm"
              as="span"
              disabled={uploading}
              onClick={() => setShowTagInput(true)}
            >
              <Icon name="Upload" size={16} />
              {uploading ? 'Uploading...' : 'Upload Files'}
            </Button>
          </label>
        </div>
      </div>

      {/* Tag Input for Upload */}
      {showTagInput && (
        <div className="mb-4 p-3 bg-[var(--color-background)] rounded-lg border border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            <Input
              value={newTags}
              onChange={(e) => setNewTags(e?.target?.value)}
              placeholder="Add tags (comma-separated, e.g., design, draft, final)"
              className="flex-1"
            />
            <label>
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploading}
              />
              <Button size="sm" as="span" disabled={uploading}>
                Upload
              </Button>
            </label>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setShowTagInput(false);
                setNewTags('');
              }}
            >
              Cancel
            </Button>
          </div>
          <p className="text-xs text-[var(--color-text-secondary)] mt-2">
            Tags help organize your files. You can filter by tags later.
          </p>
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="mb-4 flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2">
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            placeholder="Search files by name or tags..."
            className="flex-1"
            onKeyPress={(e) => e?.key === 'Enter' && handleSearch()}
          />
          <Button size="sm" variant="outline" onClick={handleSearch}>
            <Icon name="Search" size={16} />
          </Button>
        </div>
      </div>

      {/* Tag Filter Pills */}
      {availableTags?.length > 0 && (
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-sm text-[var(--color-text-secondary)]">Filter by tag:</span>
          <button
            onClick={() => setSelectedTag(null)}
            className={`px-3 py-1 rounded-full text-sm transition-colors ${
              selectedTag === null
                ? 'bg-[var(--color-primary)] text-white'
                : 'bg-[var(--color-background)] text-[var(--color-text-secondary)] hover:bg-[var(--color-card)]'
            }`}
          >
            All Files
          </button>
          {availableTags?.map((tag) => (
            <button
              key={tag}
              onClick={() => setSelectedTag(tag)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                selectedTag === tag
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-[var(--color-background)] text-[var(--color-text-secondary)] hover:bg-[var(--color-card)]'
              }`}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      {/* Files List */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-8 text-[var(--color-text-secondary)]">
            <Icon name="Loader" size={24} className="animate-spin mx-auto mb-2" />
            Loading files...
          </div>
        ) : files?.length === 0 ? (
          <div className="text-center py-8 text-[var(--color-text-secondary)]">
            <Icon name="FolderOpen" size={48} className="mx-auto mb-2 opacity-50" />
            <p>No files yet</p>
            <p className="text-sm mt-1">Upload files with tags to get started</p>
          </div>
        ) : (
          files?.map((file) => (
            <div
              key={file?.id}
              className="flex items-center justify-between p-3 bg-[var(--color-background)] rounded-lg border border-[var(--color-border)] hover:border-[var(--color-primary)] transition-colors"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Icon
                  name={getFileIcon(file)}
                  size={20}
                  className="text-[var(--color-text-secondary)]"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[var(--color-text-primary)] font-medium truncate">
                    {file?.name}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-xs text-[var(--color-text-secondary)]">
                      {formatFileSize(file?.file_size)}
                    </p>
                    {file?.tags?.length > 0 && (
                      <div className="flex items-center gap-1 flex-wrap">
                        {file?.tags?.map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    if (editingTags === file?.id) {
                      setEditingTags(null);
                    } else {
                      setEditingTags(file?.id);
                    }
                  }}
                  className="p-2 hover:bg-[var(--color-card)] rounded transition-colors"
                  title="Edit tags"
                >
                  <Icon name="Tag" size={16} className="text-[var(--color-text-secondary)]" />
                </button>
                <button
                  onClick={() => handleDownloadFile(file)}
                  className="p-2 hover:bg-[var(--color-card)] rounded transition-colors"
                  title="Download"
                >
                  <Icon name="Download" size={16} className="text-[var(--color-text-secondary)]" />
                </button>
                {isOwner && (
                  <button
                    onClick={() => handleDeleteFile(file)}
                    className="p-2 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <Icon name="Trash2" size={16} className="text-red-500" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Tag Editor Modal */}
      {editingTags && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Edit Tags</h3>
            <Input
              value={files?.find(f => f?.id === editingTags)?.tags?.join(', ') || ''}
              onChange={(e) => {
                const updatedTags = e?.target?.value?.split(',')?.map(t => t?.trim())?.filter(t => t?.length > 0);
                setFiles(prev => prev?.map(f => 
                  f?.id === editingTags ? { ...f, tags: updatedTags } : f
                ));
              }}
              placeholder="Enter tags (comma-separated)"
              className="mb-4"
            />
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setEditingTags(null);
                  fetchFiles();
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  const file = files?.find(f => f?.id === editingTags);
                  if (file) {
                    handleUpdateTags(file?.id, file?.tags);
                  }
                }}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 text-blue-700">
            <Icon name="Loader" size={16} className="animate-spin" />
            <span className="text-sm font-medium">Uploading files...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectFileManager;